import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showSuccessToast } from '../../../../../utils';
import { isDateInRange, today } from '../../../../../utils/time';
import { CloseIconButton, SimpleButton } from '../../../../common/Buttons';
import { DateInput } from '../../../../common/Inputs';
import { Row } from '../../../../common/LayoutComponents';
import { Modal, ModalBody, NewModalFooter } from '../../../../common/Modals';
import { SelectStopDropdown } from '../../../Components/SelectMemberStops';
import { SelectedStop } from '../../../Components/SelectMemberStops/common';
import { useGetStopVersions } from '../../../Stops/Versions/Queries/useGetStopVersions';
import { StopVersion } from '../../../Stops/Versions/Types';
import { useGetStopAreaVersions } from '../../Versions/Queries/useGetStopAreaVersions';
import { StopAreaVersion } from '../../Versions/Types';
import { FutureVersionsAlertPopover } from './FutureVersionsAlertPopover';
import { StopVersionsList } from './StopVersionsList';
import { useMoveQuayToStopPlace } from './useMoveQuayToStopPlace';

const testIds = {
  selectMemberStops: 'MemberStops::selectMemberStops',
  modal: 'MemberStops::modal',
  transferDateInput: 'MemberStops::transferDateInput',
  getStopVersionsButton: 'MemberStops::getStopVersionsButton',
  saveButton: 'MemberStops::saveButton',
  closeButton: 'MembersStops::closeButton',
};

type MoveQuayToStopAreaModalProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: () => void;
  readonly areaId: string;
  readonly areaPrivateCode: string;
};

type ModalState = {
  readonly selectedStop: SelectedStop | null;
  readonly selectedDate: Temporal.PlainDate;
  readonly showVersions: boolean;
};

const initialState: ModalState = {
  selectedStop: null,
  selectedDate: today(),
  showVersions: false,
};

function useFilteredStopVersions(
  stopVersions: ReadonlyArray<StopVersion> | null | undefined,
  selectedDate: Temporal.PlainDate,
): ReadonlyArray<StopVersion> | null | undefined {
  return useMemo(() => {
    if (!stopVersions || !selectedDate) {
      return stopVersions;
    }

    return stopVersions.filter((version: StopVersion) => {
      const isValidOnDate = isDateInRange(
        selectedDate,
        version.validity_start,
        version.validity_end,
      );
      const isFutureVersion =
        Temporal.PlainDate.compare(version.validity_start, selectedDate) > 0;
      return isValidOnDate || isFutureVersion;
    });
  }, [stopVersions, selectedDate]);
}

// Remove versions that end before the selected date
function useRemovePastStopAreaVersions(
  stopAreaVersions: ReadonlyArray<StopAreaVersion> | null | undefined,
  selectedDate: Temporal.PlainDate,
): ReadonlyArray<StopAreaVersion> | null | undefined {
  return useMemo(() => {
    if (!stopAreaVersions || !selectedDate) {
      return stopAreaVersions;
    }

    return stopAreaVersions?.filter(
      (version) =>
        version.validity_end === null ||
        Temporal.PlainDate.compare(version.validity_end, selectedDate) >= 0,
    );
  }, [selectedDate, stopAreaVersions]);
}

export const MoveQuayToStopAreaModal: FC<MoveQuayToStopAreaModalProps> = ({
  isOpen,
  onClose,
  onSave,
  areaId,
  areaPrivateCode,
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState<ModalState>(initialState);
  const { selectedStop, selectedDate, showVersions } = state;

  const {
    moveQuayToStopPlace,
    loading: moveLoading,
    error: moveError,
    defaultErrorHandler,
    reset: resetMoveError,
  } = useMoveQuayToStopPlace();

  const stopVersionsResult = useGetStopVersions(
    showVersions && selectedStop?.publicCode ? selectedStop.publicCode : '',
  );
  const filteredStopVersions = useFilteredStopVersions(
    stopVersionsResult.stopVersions,
    selectedDate,
  );

  // Wait for the modal to be opened before fetching stop area versions
  const stopAreaVersionsResult = useGetStopAreaVersions(
    isOpen ? areaPrivateCode : '',
  );
  const filteredStopAreaVersions = useRemovePastStopAreaVersions(
    stopAreaVersionsResult.stopAreaVersions,
    selectedDate,
  );
  const hasMultipleFutureStopAreaVersions =
    filteredStopAreaVersions?.length && filteredStopAreaVersions.length > 1;

  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  const updateState = useCallback((updates: Partial<ModalState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleClose = () => {
    resetState();

    if (resetMoveError) {
      resetMoveError();
    }
    onClose();
  };

  const handleSave = async () => {
    if (!selectedStop?.publicCode) {
      return;
    }

    const saveParams = {
      toStopPlaceId: areaId,
      quayIds: [selectedStop.quayId],
      moveQuayFromDate: selectedDate,
    };

    try {
      await moveQuayToStopPlace(saveParams);

      showSuccessToast(t(($) => $.stopAreaDetails.memberStops.moveSuccess));

      onSave();
      handleClose();
    } catch (error) {
      defaultErrorHandler(error as Error);
    }
  };

  const handleStopSelection = useCallback(
    (
      newValue: SelectedStop | null,
      formOnChange?: (value: SelectedStop | null) => void,
    ) => {
      updateState({
        selectedStop: newValue,
        showVersions: false,
      });

      if (formOnChange) {
        formOnChange(newValue);
      }
    },
    [updateState],
  );

  const onDateChange = (newDate: Temporal.PlainDate) =>
    updateState({ selectedDate: newDate });

  const handleGetStopVersions = () => {
    if (selectedStop?.publicCode) {
      updateState({ showVersions: true });
    }
  };

  const hasSelection = !!selectedStop;
  const canFetchVersions =
    selectedStop?.publicCode && !stopVersionsResult.loading;
  const shouldShowVersionsList =
    showVersions && !stopVersionsResult.loading && filteredStopVersions;

  const isSaving = moveLoading;
  const canSave = hasSelection && !isSaving;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      testId={testIds.modal}
      contentClassName="mx-4 w-full max-w-2xl rounded-lg drop-shadow-none"
    >
      <Row className="flex flex-row items-center border border-light-grey bg-background px-10 py-7">
        <h2>{t(($) => $.stopAreaDetails.memberStops.moveStopToArea)}</h2>
        {hasMultipleFutureStopAreaVersions ? (
          <FutureVersionsAlertPopover className="ml-4" />
        ) : null}
        <CloseIconButton
          className="ml-auto"
          onClick={onClose}
          testId={testIds.closeButton}
        />
      </Row>
      <ModalBody className="mx-0 my-0">
        <div className="mx-8 my-8">
          <div className="mb-2 text-sm font-bold">
            {t(($) => $.stopAreaDetails.memberStops.getStop)}
          </div>
          <SelectStopDropdown
            value={selectedStop ?? null}
            testId={testIds.selectMemberStops}
            onSelectionChange={(newValue) =>
              handleStopSelection(newValue, undefined)
            }
            areaId={areaId}
          />
        </div>

        {hasSelection && (
          <div className="items-center border-t border-light-grey bg-background px-8 py-8">
            <Row className="items-end gap-5">
              <DateInput
                value={selectedDate}
                label={t(($) => $.stopAreaDetails.memberStops.transferDate)}
                onChange={onDateChange}
                testId={testIds.transferDateInput}
                dateInputId="observation-date-input"
              />

              <SimpleButton
                inverted
                type="button"
                onClick={handleGetStopVersions}
                disabled={!canFetchVersions}
                testId={testIds.getStopVersionsButton}
              >
                {stopVersionsResult.loading
                  ? t(($) => $.loading)
                  : t(
                      ($) =>
                        $.stopAreaDetails.memberStops.actionButtons
                          .getStopVersions,
                    )}
              </SimpleButton>
            </Row>

            {shouldShowVersionsList && (
              <StopVersionsList
                versions={filteredStopVersions}
                title={t(($) => $.stopAreaDetails.memberStops.stopVersions)}
              />
            )}
          </div>
        )}

        {moveError && (
          <div className="mx-8 mb-4 rounded-sm border border-red-400 bg-red-100 p-3 text-red-700">
            {t(($) => $.stopAreaDetails.memberStops.moveError, {
              reason: moveError.message,
            })}
          </div>
        )}
      </ModalBody>
      <NewModalFooter>
        <SimpleButton
          shape="slim"
          type="button"
          onClick={handleClose}
          disabled={isSaving}
          inverted
        >
          {t(($) => $.cancel)}
        </SimpleButton>
        <SimpleButton
          shape="slim"
          className="opacity-100"
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          testId={testIds.saveButton}
        >
          {isSaving ? t(($) => $.moving) : t(($) => $.move)}
        </SimpleButton>
      </NewModalFooter>
    </Modal>
  );
};
